import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * Search powered by Shopify's native Predictive Search API.
 * The component keeps Horizon's dialog lifecycle, keyboard model, debounce and request cancellation.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} predictiveSearchResults
 * @property {HTMLButtonElement} resetButton
 * @property {HTMLElement[]} [resultsItems]
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @returns {DialogComponent | null} */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) this.#showResetButton();

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
      this.addEventListener('click', this.#handleModalClick, { signal });
      this.addEventListener('keydown', this.#trapFocus, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#activeFetch?.abort();
  }

  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') this.dialog?.toggleDialog();
  };

  #handleDialogOpen = () => {
    requestAnimationFrame(() => this.refs.searchInput.focus());
  };

  #handleDialogClose = () => {
    this.#resetSearch();
  };

  /**
   * Keeps typing friction low when a user clicks on a non-interactive part of the shelf.
   * @param {MouseEvent} event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const interactiveTarget = target.closest('a, button, input');

    if (!interactiveTarget) this.refs.searchInput.focus();
  };

  /**
   * Keeps keyboard focus within the open search shelf, including browsers that
   * briefly move focus to the document body when a native dialog wraps.
   * @param {KeyboardEvent} event
   */
  #trapFocus = (event) => {
    if (event.key !== 'Tab' || !this.dialog?.refs.dialog.open) return;

    const focusableElements = Array.from(
      this.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])')
    ).filter((element) => element instanceof HTMLElement && !element.hidden && element.getClientRects().length > 0);

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    if (!firstElement || !lastElement) return;

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  get #allResultsItems() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.refs.predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]')).filter(
        (item) => item instanceof HTMLElement
      )
    );
  }

  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems.findIndex((item) => item.getAttribute('aria-selected') === 'true');
  }

  set #currentIndex(index) {
    const items = this.#allResultsItems;
    if (!items.length) return;

    let activeItem = null;

    items.forEach((item, itemIndex) => {
      const isActive = itemIndex === index;
      item.classList.toggle('keyboard-focus', isActive && this.#isKeyboardNavigation);
      if (isActive) {
        item.setAttribute('aria-selected', 'true');
      } else {
        item.removeAttribute('aria-selected');
      }
      if (isActive) activeItem = item;
    });

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus({ preventScroll: true });
  }

  get #currentItem() {
    return this.#allResultsItems[this.#currentIndex];
  }

  /** @param {KeyboardEvent} event */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.#resetSearch();
      this.dialog?.closeDialog();
      return;
    }

    if (!this.#allResultsItems.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') return;

    const currentIndex = this.#currentIndex;
    const lastIndex = this.#allResultsItems.length - 1;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < lastIndex ? currentIndex + 1 : 0;
        break;
      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : lastIndex;
        break;
      case 'Enter': {
        const singleResult = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResult instanceof HTMLElement && singleResult.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResult.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        }
        break;
      }
    }
  };

  /**
   * Clears the query and restores the deterministic quick-links state.
   * @param {boolean} [keepFocus=true]
   */
  resetSearch = debounce((keepFocus = true) => {
    this.#resetSearch();
    if (keepFocus) this.refs.searchInput.focus();
  }, 100);

  search = debounce((event) => {
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  #resetScrollPosition() {
    requestAnimationFrame(() => this.refs.predictiveSearchResults.scrollTo({ top: 0, behavior: 'instant' }));
  }

  /** @param {'empty' | 'loading' | 'error'} state */
  #renderState(state) {
    const template = this.querySelector(`[data-search-state-template="${state}"]`);
    if (!(template instanceof HTMLTemplateElement)) return;

    this.refs.predictiveSearchResults.replaceChildren(template.content.cloneNode(true));
    this.refs.predictiveSearchResults.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
    this.#resetScrollPosition();
  }

  /** @param {string} searchTerm */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'query,product,collection');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');

    const abortController = this.#createAbortController();
    const deferredPromise = SearchUpdateEvent.createPromise();

    this.#renderState('loading');

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query: searchTerm },
        promise: deferredPromise.promise,
      })
    );

    try {
      const resultsMarkup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);

      if (abortController.signal.aborted) {
        deferredPromise.resolve({ totalCount: 0 });
        return;
      }

      if (!resultsMarkup) throw new Error('Predictive search returned no markup');

      morph(this.refs.predictiveSearchResults, resultsMarkup);
      this.refs.predictiveSearchResults.setAttribute('aria-busy', 'false');
      this.refs.searchInput.setAttribute('aria-expanded', 'true');
      this.#resetScrollPosition();

      deferredPromise.resolve({
        totalCount: this.refs.predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length,
      });
    } catch {
      deferredPromise.resolve({ totalCount: 0 });
      if (abortController.signal.aborted) return;
      this.#renderState('error');
    }
  }

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    this.#activeFetch?.abort();
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = () => {
    this.#createAbortController();
    this.#currentIndex = -1;
    this.#isKeyboardNavigation = false;
    this.refs.searchInput.value = '';
    this.#hideResetButton();
    this.#renderState('empty');
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
